/*
 Component combined of Popover and Overlay - it behaves as a popover on desktop and overlay on
 mobile

 It works best if you add PropertyPopoverTrigger as a trigger, but you can add any other component
 with handleClick prop here.

 function Test({ handleClick }: { handleClick?: () => void }) {
 function onClick() {
 handleClick && handleClick()
 }
 return <Button onClick={onClick} />
 }

 Example usage:
 <Overlay
 addSelectedValue={onSelect}
 properties={properties}
 selectedValues={selected}
 customTrigger={
 <PropertyPopoverTrigger
 icon={<Icon icon={`arrow-down-up`} />}
 variant={`outline`}
 label={``}
 />
 }
 />

 If you don't add it, PropertyPopoverTrigger will be the default trigger.

 To add x icon and allow doing something on a click, add XBtn as a prop.
 For correct styling when xBtn is included, add your chosen button variant to XBtn as a prop
 and add ghost variant to PropertyPopoverTrigger

 If you add selectAll as a prop, selectAllProperty will be added as a first item in popover

 You can add date picker component as nestedDatePicker prop
 */

import { PopoverProperty } from '@/components/common/propertyPopoverOverlay/Property.tsx'
import { ReactNode } from 'react'
import { Overlay } from '@/components/common/propertyPopoverOverlay/overlay/Overlay.tsx'
import PropertyPopover from '@/components/common/propertyPopoverOverlay/PropertyPopover.tsx'
import { ButtonSizeT, ButtonVariantT } from '@/components/ui/Button.tsx'
import { usePopoverOverlayContext } from '@/components/common/propertyPopoverOverlay/PopoverOverlayContext.ts'

export type XBtnT = {
  onClick: () => void
  variant?: ButtonVariantT
  size?: ButtonSizeT
}

export type PopoverOverlayT = {
  addSelectedValue: (val: string) => void
  closeOnSelect?: boolean
  customTrigger?: ReactNode
  fallbackMessage?: string
  // Optional header rendered at the top of the popover content. Used for
  // contextual labels like "Wybór projektu/etapu" above the stage list.
  popoverHeader?: ReactNode
  // Title shown in the mobile sheet header (Linear-style: title + ✕ close).
  // Desktop anchored popover ignores it. Optional — call sites add it
  // incrementally; the sheet shows an ✕-only header when absent.
  title?: ReactNode
  onClose?: () => void
  onSelectParent?: (val: string) => void
  properties: PopoverProperty[]
  removeSelectedValue?: (value: string) => void
  selectAll?: () => void
  selectedValues: string[]
  searchLabel?: string
  showSearch?: boolean
  showAllSearchResults?: boolean
  XBtn?: XBtnT
  onOpen?: () => void
  className?: string
  handleSearch?: (val: string) => void
  modal?: boolean
  debug?: boolean
  selectedParent?: string | null
  removeSelectedParent?: () => void
}

export default function PopoverOverlay(props: PopoverOverlayT) {
  const { forceAnchored } = usePopoverOverlayContext()

  // Opt-in via PopoverOverlayContext (e.g. the New Issue dialog's chip
  // strip): render the anchored desktop popover even on mobile so the
  // popover opens right above the trigger instead of sliding up from
  // the screen bottom and visually decoupling from the chip.
  if (forceAnchored) {
    return <PropertyPopover {...props} />
  }

  return (
    <>
      <div className={`min-w-0 md:hidden`}>
        <Overlay {...props} />
      </div>

      <div className={`hidden md:flex`}>
        <PropertyPopover {...props} />
      </div>
    </>
  )
}
